import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  HiOutlineUserCircle, HiOutlineArrowRightEndOnRectangle, HiOutlineCog6Tooth,
  HiOutlineBuildingStorefront, HiOutlineUsers, HiOutlineHome
} from 'react-icons/hi2';
import { useAuth } from '../context/AuthContext';
import { useRestaurant } from '../context/RestaurantContext';

const initials = (n) => (n || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';

const TopBar = ({ title }) => {
  const nav = useNavigate();
  const { user, logout, isOwner, isOwnerOrManager } = useAuth();
  const { activeRestaurant } = useRestaurant();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef();

  useEffect(() => {
    const onClick = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, []);

  return (
    <div className="topbar">
      <div className="topbar-left">
        <Link to="/" className="topbar-brand">
          <div className="topbar-brand-mark hide-on-mobile">RM</div>
          <span className="topbar-brand-text">Restaurant Manager</span>
        </Link>
        {title && <span className="hide-on-mobile" style={{ color: 'var(--text-muted)', fontSize: 14, marginLeft: 4 }}>· {title}</span>}
      </div>

      <div className="topbar-right">
        {/* Active restaurant pill — only shown once one is picked */}
        {activeRestaurant && (
          <div
            className="topbar-rest-pill"
            onClick={() => isOwner && nav('/restaurants')}
            style={{ cursor: isOwner ? 'pointer' : 'default' }}
            title={isOwner ? 'Switch restaurant' : activeRestaurant.name}
          >
            {activeRestaurant.logo
              ? <img src={activeRestaurant.logo} alt="" />
              : <div style={{
                  width: 26, height: 26, borderRadius: 7, flexShrink: 0,
                  background: 'var(--ink-900)', color: 'var(--lime-400)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13
                }}>
                  <HiOutlineBuildingStorefront size={14} />
                </div>
            }
            <span className="topbar-rest-pill-name">{activeRestaurant.name}</span>
          </div>
        )}

        {/* Dashboard icon — owner+manager, requires an active restaurant */}
        {isOwnerOrManager && activeRestaurant && (
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/dashboard')} title="Dashboard">
            <HiOutlineHome size={18} />
          </button>
        )}

        {/* Users icon — OWNER only, requires an active restaurant (per-restaurant management) */}
        {isOwner && activeRestaurant && (
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/users')} title="Manage Users">
            <HiOutlineUsers size={18} />
          </button>
        )}

        {/* Manage restaurants — OWNER only */}
        {isOwner && (
          <button className="btn btn-ghost btn-sm" onClick={() => nav('/restaurants/manage')} title="Manage Restaurants">
            <HiOutlineCog6Tooth size={18} />
          </button>
        )}

        {/* User menu */}
        <div ref={menuRef} style={{ position: 'relative' }}>
          <div className="user-chip" onClick={() => setMenuOpen(o => !o)}>
            <div className="user-chip-avatar">{initials(user?.name)}</div>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span className="user-chip-name">{user?.name}</span>
              <span className="user-chip-role">{user?.role === 'owner' ? 'Admin' : user?.role}</span>
            </div>
          </div>
          {menuOpen && (
            <div style={{
              position: 'absolute', right: 0, top: 'calc(100% + 8px)',
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 14, boxShadow: 'var(--shadow-lg)',
              minWidth: 200, padding: 6, zIndex: 60, overflow: 'hidden'
            }}>
              <button onClick={() => { setMenuOpen(false); nav('/profile'); }} style={menuItem}>
                <HiOutlineUserCircle size={18} /> My Profile
              </button>
              <button onClick={() => { setMenuOpen(false); logout(); nav('/signin'); }} style={{ ...menuItem, color: 'var(--bad)' }}>
                <HiOutlineArrowRightEndOnRectangle size={18} /> Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const menuItem = {
  display: 'flex', alignItems: 'center', gap: 10,
  width: '100%', padding: '10px 12px', border: 'none',
  background: 'transparent', cursor: 'pointer',
  fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
  borderRadius: 8, textAlign: 'left', color: 'var(--text)',
  transition: 'background 0.15s'
};

export default TopBar;